import JSZip from 'jszip';
import { logError, logInfo } from '../logger';
import { parseGeoJSON } from './geojsonParser';
import { validateGeoJSON } from './geojsonValidator';

/**
 * Process a ZIP file containing GeoJSON and images
 * @param {File} file - ZIP file
 * @returns {Promise<Object>} - Extracted data
 */
export const processZipWithImages = async (file) => {
  try {
    logInfo(`Processing ZIP file: ${file.name}, size: ${file.size} bytes`);
    
    // Load the ZIP file
    const zip = new JSZip();
    const zipContents = await zip.loadAsync(file);
    
    // Find GeoJSON files
    const geojsonFiles = Object.keys(zipContents.files).filter(
      filename => filename.toLowerCase().endsWith('.geojson')
    );
    
    // Find image files
    const imageFiles = Object.keys(zipContents.files).filter(
      filename => {
        const lowerFilename = filename.toLowerCase();
        return lowerFilename.endsWith('.jpg') || 
               lowerFilename.endsWith('.jpeg') || 
               lowerFilename.endsWith('.png');
      }
    );
    
    logInfo(`Found ${geojsonFiles.length} GeoJSON files, ${imageFiles.length} images, and ${metaFile ? 1 : 0} metadata files`);
    
    // Process metadata if available
    let metadata = {};
    if (metaFile) {
      try {
        const metaContent = await zipContents.files[metaFile].async('text');
        metadata = JSON.parse(metaContent);
        logInfo('Metadata parsed successfully');
      } catch (error) {
        logError('Failed to parse metadata:', error);
      }
    }
    
    // Process GeoJSON files
    let points = [];
    let routes = [];
    let layers = new Set();
    
    for (const geojsonFile of geojsonFiles) {
      try {
        const content = await zipContents.files[geojsonFile].async('text');
        const data = JSON.parse(content);
        
        // Validate GeoJSON
        const validation = validateGeoJSON(data);
        if (!validation.isValid) {
          logError(`Invalid GeoJSON in ${geojsonFile}:`, validation.error);
          continue;
        }
        
        // Parse GeoJSON
        const result = await parseGeoJSON(data);
        points = [...points, ...result.points];
        routes = [...routes, ...result.routes];
        result.layers.forEach(layer => layers.add(layer));
      } catch (error) {
        logError(`Failed to process GeoJSON ${geojsonFile}:`, error);
      }
    }
    
    // Process images
    const images = [];
    for (const imageFile of imageFiles) {
      try {
        const imageData = await zipContents.files[imageFile].async('blob');
        const imageUrl = URL.createObjectURL(imageData);
        
        try {
          const img = new Image();
          img.src = imageUrl;
          await img.decode();
          
          const dimensionKey = `${img.naturalWidth}x${img.naturalHeight}`;
          const filename = imageFile.split('/').pop();
          const poiIdMatch = filename.match(/(?:poi_|^)([a-zA-Z0-9-_]+)(?:_photo|\.)/i);
          const poiId = poiIdMatch ? poiIdMatch[1] : null;
          
          // Add image to the images array
          images.push({
            id: `img_${Math.random().toString(36).substring(2, 9)}`,
            name: filename,
            data: imageUrl,
            poiId,
            dimensions: dimensionKey
          });
          
          logInfo(`Processed image: ${filename} (${dimensionKey})`);
        } catch (dimError) {
          logError(`Failed to get dimensions for ${imageFile}:`, dimError);
          
          // Still add the image without dimensions
          const filename = imageFile.split('/').pop();
          const poiIdMatch = filename.match(/(?:poi_|^)([a-zA-Z0-9-_]+)(?:_photo|\.)/i);
          const poiId = poiIdMatch ? poiIdMatch[1] : null;
          
          // Add image to the images array
          images.push({
            id: `img_${Math.random().toString(36).substring(2, 9)}`,
            name: filename,
            data: imageUrl,
            poiId
          });
        }
      } catch (error) {
        logError(`Failed to process image ${imageFile}:`, error);
      }
    }
    
    return {
      points,
      routes,
      layers: Array.from(layers),
      images,
      metadata
    };
  } catch (error) {
    logError('Failed to process ZIP file:', error);
    throw error;
  }
};